import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { calculateFinancing, formatCurrency } from '../../Succession/Financing/financingCalculations';
import FinancingResultsPanel from './FinancingResultsPanel';

// Read-only view of a saved FinancingSnapshot rendered as a side-by-side
// input + results layout — matches what the owner sees at
// /succeedo/financing-calculator without dragging in the full
// FinancingCalculatorPage's stateful machinery.
//
// Inputs come from snapshot.inputs (purchase_price, down_payment,
// loan_term_years, interest_rate, lender_name). The amortization schedule
// isn't persisted to the snapshot, so we recompute it on-the-fly via
// calculateFinancing() with a synthetic lender that yields the stored rate.
//
// Used by OwnerViewModal when snapshotType === 'financing_snapshot'.

const formatPercent = (decimal) => `${(Number(decimal || 0) * 100).toFixed(1)}%`;

const StaticField = ({ label, value, hint }) => (
  <div className="space-y-1">
    <div className="font-mono text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
      {label}
    </div>
    <div className="text-[16px] font-medium text-[hsl(var(--foreground))]">{value}</div>
    {hint && <div className="text-[11px] text-[hsl(var(--muted-foreground))]/70">{hint}</div>}
  </div>
);

StaticField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  hint: PropTypes.string,
};

StaticField.defaultProps = {
  hint: null,
};

const Pill = ({ active, children }) => (
  <span
    className={`inline-flex items-center justify-center px-3 py-1.5 rounded-full text-[12px] font-mono uppercase tracking-wide border ${
      active
        ? 'bg-[hsl(var(--foreground))] text-[hsl(var(--background))] border-[hsl(var(--foreground))]'
        : 'bg-[hsl(var(--card))] text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]'
    }`}
  >
    {children}
  </span>
);

Pill.propTypes = {
  active: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
};

const TERM_PILL_OPTIONS = [3, 5, 7, 10];

const FinancingOwnerSnapshotView = ({ snapshot }) => {
  const inputs = snapshot?.inputs || {};
  const results = snapshot?.results || {};

  // Real shape from FinancingCalculatorPage.handleSaveSnapshot:
  //   inputs.{purchase_price, down_payment, down_payment_mode,
  //           down_payment_percent, loan_term_years, lender_name,
  //           interest_rate (decimal)}
  //   results.{loan_amount, monthly_payment, total_interest, total_cost,
  //            effective_rate (decimal), amortization_months}
  const purchasePrice = Number(inputs.purchase_price || 0);
  const downPayment = Number(inputs.down_payment || 0);
  const downPaymentPercent = Number(
    inputs.down_payment_percent ||
      (purchasePrice > 0 ? (downPayment / purchasePrice) * 100 : 0),
  );
  const loanAmount = Number(results.loan_amount || (purchasePrice - downPayment) || 0);
  const loanTerm = Number(inputs.loan_term_years || 0);
  const interestRate = Number(inputs.interest_rate || results.effective_rate || 0);
  const lenderName = inputs.lender_name || snapshot?.lender_name || 'Custom Rate';

  // Recompute the amortization schedule on the fly. The snapshot persists
  // monthly_payment / total_interest / total_cost but NOT the year-by-year
  // schedule needed for the chart in FinancingResultsPanel. Fabricate a
  // synthetic lender with `roi: interestRate` so calculateFinancing returns
  // the exact stored rate (rate-band lookup is bypassed when bands is empty).
  const calculations = useMemo(() => {
    if (loanAmount <= 0 || loanTerm <= 0) return null;
    const syntheticLender = { roi: interestRate, rateBands: [] };
    return calculateFinancing(syntheticLender, loanAmount, loanTerm * 12);
  }, [loanAmount, loanTerm, interestRate]);

  // All-equity edge case: no loan to service.
  if (loanAmount === 0) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-8 text-center">
          <div className="font-mono text-[11px] uppercase text-[hsl(var(--muted-foreground))] mb-2">
            ALL-EQUITY DEAL
          </div>
          <div className="font-serif text-[32px] font-light text-[hsl(var(--foreground))] mb-1">
            {formatCurrency(purchasePrice)}
          </div>
          <p className="text-[14px] text-[hsl(var(--muted-foreground))]">
            100% buyer equity. No debt service.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
      {/* Page header (mirrors FinancingCalculatorPage chrome) */}
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))]" />
          <span className="font-mono text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
            FINANCING CALCULATOR
          </span>
        </div>
        <h1 className="font-serif text-[42px] font-light text-[hsl(var(--foreground))] tracking-[-0.02em]">
          Model Your Deal Financing
        </h1>
        <p className="text-[15px] text-[hsl(var(--muted-foreground))]">
          See what a buyer&apos;s financing looks like — or model your own acquisition.
        </p>
      </div>

      {/* Side-by-side: input summary on the left, results on the right */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left column — static input summary, mirrors FinancingInputCard */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-6 space-y-6">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))]" />
              <span className="font-mono text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
                DEAL PARAMETERS
              </span>
            </div>

            <StaticField
              label="Business Purchase Price"
              value={formatCurrency(purchasePrice)}
              hint="From the owner's most recent valuation estimate"
            />

            <div className="space-y-2">
              <StaticField
                label="Down Payment"
                value={`${formatCurrency(downPayment)} · ${downPaymentPercent.toFixed(0)}%`}
              />
              <div className="flex flex-wrap gap-2">
                {[25, 30, 40].map((p) => (
                  <Pill key={p} active={Math.round(downPaymentPercent) === p}>{`${p}%`}</Pill>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <div className="font-mono text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
                Loan Term
              </div>
              <div className="flex flex-wrap gap-2">
                {TERM_PILL_OPTIONS.map((yrs) => (
                  <Pill key={yrs} active={loanTerm === yrs}>{`${yrs} YRS`}</Pill>
                ))}
              </div>
            </div>

            <StaticField
              label="Interest Rate"
              value={formatPercent(interestRate)}
              hint="Based on lender quote at time of save"
            />

            {/* Lender card — mirrors the radio list, but only the chosen lender is shown */}
            <div className="bg-[hsl(var(--muted))]/30 border border-[hsl(var(--border))] rounded-[12px] p-4 space-y-1">
              <div className="font-mono text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
                Selected Lender
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[15px] font-medium text-[hsl(var(--foreground))]">
                  {lenderName}
                </span>
                <span className="font-mono text-[12px] uppercase tracking-wide text-[hsl(var(--succeedo-orange))] bg-[hsl(var(--succeedo-orange))]/10 px-2 py-0.5 rounded">
                  {formatPercent(interestRate)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right column — reuses FinancingResultsPanel, fed with recomputed
            calculations from the snapshot's stored values. */}
        <div className="lg:col-span-3">
          <FinancingResultsPanel
            calculations={calculations}
            loanAmount={loanAmount}
            interestRate={interestRate}
            loanTerm={loanTerm}
            readOnly
          />
        </div>
      </div>
    </div>
  );
};

FinancingOwnerSnapshotView.propTypes = {
  snapshot: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    inputs: PropTypes.object,
    results: PropTypes.object,
    lender_name: PropTypes.string,
  }).isRequired,
};

export default FinancingOwnerSnapshotView;
